import React, { useEffect, useRef, useState } from 'react';
import { Box, Breadcrumbs, Button, Divider, Link, List, ListItem, ListItemText, ListItemIcon, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ensureLogin, listGlobalStorage, mkdirGlobalStorage, uploadGlobalStorage, deleteGlobalStorage, renameGlobalStorage, getApiHost } from '../utils/api';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Templates = () => {
  const [path, setPath] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [previewEntry, setPreviewEntry] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        await ensureLogin();
        // Carrega raiz global inicialmente
        await load('');
      } catch (e) {
        // ignorar
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await load(path);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const load = async (relPath = '') => {
    try {
      setLoading(true);
      const resp = await listGlobalStorage(relPath);
      const itemsRaw = Array.isArray(resp) ? resp : (resp?.items || []);
      const normalized = (itemsRaw || []).map((it) => {
        const isDir = it?.type === 'dir' || it?.isDir === true;
        return {
          name: it?.name ?? '',
          isDir,
          url: it?.url || null,
          path: `${relPath ? relPath + '/' : ''}${it?.name ?? ''}`,
        };
      });
      setEntries(normalized);
      setPath(Array.isArray(resp) ? relPath : (resp?.path ?? relPath));
    } catch (e) {
      alert('Falha ao carregar templates.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    const name = prompt('Nome da pasta:');
    if (!name) return;
    try {
      await mkdirGlobalStorage(path || '', name);
      await load(path || '');
    } catch (e) {
      alert('Não foi possível criar a pasta.');
    }
  };

  const handleUpload = async (evt) => {
    const files = Array.from(evt.target.files || []);
    if (!files.length) return;
    try {
      await uploadGlobalStorage(path || '', files);
      await load(path || '');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      alert('Falha ao carregar ficheiros.');
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Apagar "${entry.name}"?`)) return;
    try {
      await deleteGlobalStorage(entry.path);
      await load(path || '');
    } catch (e) {
      alert('Falha ao apagar entrada.');
    }
  };

  const isImage = (name) => /\.(png|jpg|jpeg|gif|bmp|webp)$/i.test(name || '');
  const isPdf = (name) => /\.(pdf)$/i.test(name || '');

  const handlePreview = (entry) => {
    if (entry.isDir) return;
    if (isImage(entry.name) || isPdf(entry.name)) setPreviewEntry(entry);
    else window.open(`${getApiHost()}${entry.url}`, '_blank');
  };

  const handleRename = async (entry) => {
    const newName = prompt('Novo nome:', entry.name);
    if (!newName || newName === entry.name) return;
    try {
      await renameGlobalStorage(entry.path, newName);
      await load(path || '');
    } catch (e) {
      alert('Falha ao renomear.');
    }
  };

  const goUp = () => {
    const segs = (path || '').split('/').filter(Boolean);
    const newPath = segs.slice(0, -1).join('/');
    setPath(newPath);
  };

  

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Templates (Global)</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="text" startIcon={<ArrowBackIcon />} onClick={goUp} disabled={!path}>Voltar</Button>
          <Button variant="outlined" onClick={handleCreateFolder}>Nova Pasta</Button>
          <Button variant="outlined" component="label">
            Upload
            <input ref={fileInputRef} type="file" multiple hidden onChange={handleUpload} />
          </Button>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" onClick={() => setPath('')} style={{ cursor: 'pointer' }}>Raiz</Link>
        {path.split('/').filter(Boolean).map((seg, idx, arr) => {
          const newPath = arr.slice(0, idx + 1).join('/');
          return (
            <Link key={idx} underline="hover" color="inherit" onClick={() => setPath(newPath)} style={{ cursor: 'pointer' }}>{seg}</Link>
          );
        })}
      </Breadcrumbs>
      <List>
        {loading && (<ListItem><ListItemText primary="A carregar..." /></ListItem>)}
        {!loading && entries.length === 0 && (<ListItem><ListItemText primary="Vazio." /></ListItem>)}
        {!loading && entries.map((entry) => (
          <ListItem key={entry.path} divider
            secondaryAction={
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!entry.isDir && (
                  <Button size="small" component="a" href={`${getApiHost()}${entry.url}`} target="_blank" download>Baixar</Button>
                )}
                {!entry.isDir && (
                  <Button size="small" onClick={() => handlePreview(entry)}>Pré-visualizar</Button>
                )}
                <Button size="small" onClick={() => handleRename(entry)}>Renomear</Button>
                <Button size="small" color="error" onClick={() => handleDelete(entry)}>Apagar</Button>
              </Box>
            }
          >
            <ListItemIcon>
              {entry.isDir ? <FolderIcon /> : <InsertDriveFileIcon />}
            </ListItemIcon>
            <ListItemText primary={entry.name} secondary={entry.isDir ? 'Pasta' : 'Ficheiro'} onClick={() => entry.isDir && setPath(entry.path)} style={{ cursor: entry.isDir ? 'pointer' : 'default' }} />
          </ListItem>
        ))}
      </List>

      <Dialog open={!!previewEntry} onClose={() => setPreviewEntry(null)} fullWidth maxWidth="md">
        <DialogTitle>Pré-visualização</DialogTitle>
        <DialogContent>
          {previewEntry && isImage(previewEntry.name) && (
            <img alt={previewEntry.name} src={`${getApiHost()}${previewEntry.url}`} style={{ width: '100%', height: 'auto' }} />
          )}
          {previewEntry && isPdf(previewEntry.name) && (
            <object data={`${getApiHost()}${previewEntry.url}`} type="application/pdf" width="100%" height="600">
              <p>Não foi possível mostrar o PDF. <a href={`${getApiHost()}${previewEntry.url}`} target="_blank" rel="noreferrer">Abrir em nova aba</a>.</p>
            </object>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewEntry(null)}>Fechar</Button>
          {previewEntry && (
            <Button href={`${getApiHost()}${previewEntry.url}`} target="_blank" rel="noreferrer">Abrir no navegador</Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Templates;
